import { Suspense, lazy, useRef, useState } from 'react';
import type { CalculatorMatchSelection } from '@nowscore/core';
import type { MatchDetailDto } from '../api';
import { useSwipeToClose } from '../hooks/useSwipeToClose';
import { formatKickoff } from '../lib/format';
import type { OddsView } from '../lib/odds';
import { AiAnalysisPanel, LeagueCompare, RecentList, TeamAnalysis } from './Analysis';
import { OddsComparison } from './Bookmakers';
import { StatusBadge } from './Chrome';
import { LotteryPanel } from './Lottery';
const M1Panel = lazy(() => import('./M1').then((mod) => ({ default: mod.M1Panel })));

type DetailTab = 'lottery' | 'bookmakers' | 'm1' | 'overview' | 'history';

const TABS: Array<{ key: DetailTab; label: string }> = [
  { key: 'lottery', label: '竞彩赔率' },
  { key: 'bookmakers', label: '威廉·立博' },
  { key: 'm1', label: 'M1 相似度' },
  { key: 'overview', label: '球队分析' },
  { key: 'history', label: '近期/交锋' },
];

interface DetailSheetProps {
  match: MatchDetailDto;
  oddsView: OddsView;
  onOddsViewChange: (view: OddsView) => void;
  onAnalyze: () => void;
  loading?: boolean;
  error?: string;
  calculatorSelection?: CalculatorMatchSelection | null;
  onCalculatorSelectionChange?: (selection: CalculatorMatchSelection) => void;
  onClose: () => void;
}

/**
 * 全屏上滑详情面板：
 * - 顶部拖杆 / 头部 / Tab 吸顶，内容区独立滚动；
 * - 内容滚到顶部后继续下拉 >120px（或速度 >0.6px/ms）关闭；
 * - ESC 关闭、锁背景滚动、role=dialog。
 */
export function DetailSheet({ match, oddsView, onOddsViewChange, onAnalyze, loading = false, error = "", calculatorSelection, onCalculatorSelectionChange, onClose }: DetailSheetProps) {
  const [tab, setTab] = useState<DetailTab>('lottery');
  const scrollRef = useRef<HTMLDivElement>(null);
  const swipe = useSwipeToClose(onClose, { scrollRef, distance: 120, velocity: 0.6 });
  return <div className="sheet-backdrop detail-backdrop" onClick={onClose}>
    <section
      className={'sheet detail-sheet' + (swipe.closing ? ' closing' : '')}
      style={{ transform: swipe.offset > 0 ? 'translateY(' + swipe.offset + 'px)' : undefined }}
      role="dialog"
      aria-modal="true"
      aria-label={match.homeTeamName + ' vs ' + match.awayTeamName}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="detail-sticky" onTouchStart={swipe.onTouchStart} onTouchMove={swipe.onTouchMove} onTouchEnd={swipe.onTouchEnd}>
        <div className="sheet-handle" aria-hidden="true" />
        <header className="detail-head">
          <div className="detail-head-main">
            <div className="detail-head-tags">
              <span className={'lottery-pill' + (match.lottery ? '' : ' missing')}>{match.lottery?.number ?? '未配号'}</span>
              <span className="league-pill">{match.leagueName ?? match.leagueCode}</span>
              <StatusBadge match={match} />
            </div>
            <h2>{match.homeTeamName} <small>vs</small> {match.awayTeamName}</h2>
            <span className="detail-time">{formatKickoff(match.kickoffTime)}{match.lottery?.businessDate ? ' · 批次 ' + match.lottery.businessDate : ''}</span>
          </div>
          <div className="detail-head-score">
            <b>{match.score.home ?? '—'} : {match.score.away ?? '—'}</b>
            <span>半场 {match.score.halfHome ?? '—'}:{match.score.halfAway ?? '—'} · 角球 {match.live?.homeCorners ?? 0}:{match.live?.awayCorners ?? 0}</span>
          </div>
          <div className="detail-head-actions"><button type="button" className="ai-analyze-button compact" onClick={onAnalyze}>⚡ AI 分析</button><button type="button" className="close-button" onClick={onClose} aria-label="关闭详情">关闭</button></div>
        </header>
        <nav className="detail-tabs" role="tablist" aria-label="详情分类">
          {TABS.map((item) => <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={tab === item.key}
            className={tab === item.key ? 'active' : ''}
            onClick={() => { setTab(item.key); scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); }}
          >{item.label}</button>)}
        </nav>
      </div>
      <div className="detail-body" ref={scrollRef} aria-busy={loading}>
        {loading && <div className="detail-loading" role="status" aria-live="polite" aria-busy="true"><div className="analysis-progress"><div className="analysis-progress-head"><span>正在加载比赛详情、历史与交锋</span><b>读取中</b></div><div className="analysis-progress-track indeterminate"><i /></div></div><div className="detail-skeleton"><i /><i /><i /><i /><i /></div></div>}
        {!loading && error && <div className="state-box error-box"><b>详情未完整加载</b><span>{error}</span><span>已保留赛程基础信息，可关闭后重试。</span></div>}
        {!loading && tab === 'lottery' && <LotteryPanel match={match} calculatorSelection={calculatorSelection} onCalculatorSelectionChange={onCalculatorSelectionChange} />}
        {!loading && tab === 'bookmakers' && <OddsComparison match={match} view={oddsView} onViewChange={onOddsViewChange} />}
        <Suspense fallback={<div className="state-box">正在加载 M1…</div>}>{!loading && tab === 'm1' && <M1Panel match={match} />}</Suspense>
        {!loading && tab === 'overview' && <section className="analysis-section">
          <AiAnalysisPanel match={match} />
          <TeamAnalysis team={match.homeTeamName} analysis={match.analysis.home} />
          <TeamAnalysis team={match.awayTeamName} analysis={match.analysis.away} />
          <LeagueCompare match={match} />
          <div className="h2h-summary">
            <div><span>历史交锋</span><b>{match.analysis.headToHead.played} 场</b></div>
            {match.analysis.headToHead.matches.slice(0, 5).map((item) => <HistoricalRow key={item.scheduleId} match={item} />)}
          </div>
        </section>}
        {!loading && tab === 'history' && <section className="history-section">
          <RecentList title={match.homeTeamName + ' 近期比赛'} rows={match.recent.home} />
          <RecentList title={match.awayTeamName + ' 近期比赛'} rows={match.recent.away} />
        </section>}
      </div>
    </section>
  </div>;
}

function HistoricalRow({ match }: { match: MatchDetailDto['analysis']['headToHead']['matches'][number] }) {
  return <article className="recent-match">
    <div className="recent-row">
      <time>{formatKickoff(match.kickoffTime)}</time>
      <span>{match.homeTeamName}</span>
      <b>{match.score.home ?? '—'}:{match.score.away ?? '—'}</b>
      <span>{match.awayTeamName}</span>
    </div>
  </article>;
}
